import { useState, useEffect, useCallback } from 'react';
import { api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';

type EquipmentType = 'panel' | 'inverter';

interface PanelGroup {
  id: string;
  manufacturer: string;
  model: string;
  technology: string | null;
  nominalPowerW: number | string | null;
  panelCount: number | null;
  notes: string | null;
}
interface InverterGroup {
  id: string;
  manufacturer: string;
  model: string;
  inverterCount: number;
  nominalPowerKw: number | string;
  modbusProtocol: string | null;
  modbusHost: string | null;
  modbusPort: number | null;
  modbusUnitId: number | null;
  unitIds: string | null;
  notes: string | null;
}
type Group = PanelGroup | InverterGroup;

interface Props {
  type: EquipmentType;
  assetId: string;
}

function useEquipmentColors(isDark: boolean) {
  return {
    cardBg: isDark ? '#1A1A1A' : '#fff',
    cardBorder: isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8',
    textMain: isDark ? '#E0E0E0' : '#1A1A1A',
    textMuted: isDark ? '#8E8E93' : '#6B6B70',
    mutedText: isDark ? '#6B6B70' : '#9B9BA4',
    tableHeaderBg: isDark ? 'rgba(255,255,255,0.03)' : '#F9F9FB',
    tableBorder: isDark ? 'rgba(255,255,255,0.05)' : '#F0F0F2',
    iconBg: isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7',
    inputBg: isDark ? '#1A1A1A' : '#fff',
    inputBorder: isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8',
    modalOverlay: 'rgba(0,0,0,0.5)',
    footerBg: isDark ? 'rgba(255,255,255,0.03)' : '#FAFAFA',
    footerBorder: isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8',
    sectionBg: isDark ? 'rgba(255,255,255,0.02)' : '#F9F9FB',
    sectionBorder: isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8',
    cancelBg: isDark ? 'rgba(255,255,255,0.04)' : '#fff',
    badgeBg: isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7',
  };
}

export default function EquipmentGroupsSection({ type, assetId }: Props) {
  const { isDark } = useTheme();
  const clr = useEquipmentColors(isDark);
  const meta = {
    panel: {
      label: 'Panel Groups',
      subtitle: 'Solar panel models installed on this site (informational; not used in forecast formula)',
      endpoint: 'panels',
      icon: 'ri-layout-grid-line',
      iconBg: 'rgba(13,122,60,0.08)',
      iconColor: '#0D7A3C',
    },
    inverter: {
      label: 'Inverter Groups',
      subtitle: 'Inverter sectors + their Modbus configuration (collectors poll these every 5 min)',
      endpoint: 'inverters',
      icon: 'ri-pulse-line',
      iconBg: 'rgba(31,91,143,0.08)',
      iconColor: '#1F5B8F',
    },
  }[type];

  const [rows, setRows] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { showToast } = useToast();

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<Group[]>(`/assets/${assetId}/${meta.endpoint}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      showToast({ type: 'error', message: getApiErrorMessage(e, 'Failed to load equipment groups') });
    } finally {
      setLoading(false);
    }
  }, [assetId, meta.endpoint, showToast]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleAdd = () => { setEditingGroup(null); setModalOpen(true); };
  const handleEdit = (g: Group) => { setEditingGroup(g); setModalOpen(true); };
  const handleSaved = () => { setModalOpen(false); fetchRows(); };
  const handleDelete = async (g: Group) => {
    const label = type === 'panel'
      ? `${g.manufacturer} ${g.model}`
      : `${g.manufacturer} ${(g as InverterGroup).modbusPort ? ':' + (g as InverterGroup).modbusPort : ''}`.trim();
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/assets/${assetId}/${meta.endpoint}/${g.id}`);
      showToast({ type: 'success', message: 'Deleted' });
      fetchRows();
    } catch (e) {
      showToast({ type: 'error', message: getApiErrorMessage(e, 'Delete failed') });
    }
  };

  return (
    <div className="rounded-xl p-5 mb-6" style={{ background: clr.cardBg, border: `1px solid ${clr.cardBorder}` }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: meta.iconBg }}>
            <i className={`${meta.icon} text-sm`} style={{ color: meta.iconColor }}></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: clr.textMain }}>{meta.label}</h3>
            <p className="text-xs" style={{ color: clr.textMuted }}>{meta.subtitle}</p>
          </div>
        </div>
        <button
          onClick={handleAdd}
          className="text-xs font-medium px-3 py-1.5 rounded-md cursor-pointer transition-colors whitespace-nowrap"
          style={{ background: '#D12229', color: '#fff' }}
        >
          <i className="ri-add-line text-xs mr-1"></i>
          Add {type === 'panel' ? 'panel' : 'inverter'} group
        </button>
      </div>

      {loading && rows.length === 0 && (
        <div className="text-center py-6 text-xs" style={{ color: clr.textMuted }}>
          <i className="ri-loader-4-line animate-spin mr-1"></i> Loading...
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="text-center py-8 text-xs" style={{ color: clr.mutedText }}>
          No {type === 'panel' ? 'panel' : 'inverter'} groups configured.
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ background: clr.tableHeaderBg, color: clr.textMuted }}>
                <th className="text-left px-3 py-2 font-medium">Manufacturer</th>
                <th className="text-left px-3 py-2 font-medium">Model</th>
                {type === 'panel' && <th className="text-right px-3 py-2 font-medium">Power, W</th>}
                {type === 'panel' && <th className="text-right px-3 py-2 font-medium">Count</th>}
                {type === 'inverter' && <th className="text-right px-3 py-2 font-medium">Power, kW</th>}
                {type === 'inverter' && <th className="text-right px-3 py-2 font-medium">Count</th>}
                {type === 'inverter' && <th className="text-left px-3 py-2 font-medium">Modbus</th>}
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.id} style={{ borderTop: `1px solid ${clr.tableBorder}` }}>
                  <td className="px-3 py-2" style={{ color: clr.textMain }}>{g.manufacturer}</td>
                  <td className="px-3 py-2" style={{ color: clr.textMain }}>{g.model}</td>
                  {type === 'panel' && (
                    <>
                      <td className="px-3 py-2 text-right" style={{ color: clr.textMain }}>
                        {(g as PanelGroup).nominalPowerW != null ? Number((g as PanelGroup).nominalPowerW).toFixed(0) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right" style={{ color: clr.textMain }}>
                        {(g as PanelGroup).panelCount ?? '—'}
                      </td>
                    </>
                  )}
                  {type === 'inverter' && (
                    <>
                      <td className="px-3 py-2 text-right" style={{ color: clr.textMain }}>
                        {Number((g as InverterGroup).nominalPowerKw).toFixed(0)}
                      </td>
                      <td className="px-3 py-2 text-right" style={{ color: clr.textMain }}>
                        {(g as InverterGroup).inverterCount}
                      </td>
                      <td className="px-3 py-2" style={{ color: clr.textMuted, fontFamily: 'monospace' }}>
                        {(g as InverterGroup).modbusHost
                          ? `${(g as InverterGroup).modbusHost}:${(g as InverterGroup).modbusPort ?? '—'}`
                          : <span style={{ color: clr.mutedText }}>not configured</span>}
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => handleEdit(g)} className="text-xs px-2 py-1 rounded cursor-pointer hover:bg-gray-100" style={{ color: '#1F5B8F' }}>
                      <i className="ri-edit-line text-xs mr-0.5"></i>Edit
                    </button>
                    <button onClick={() => handleDelete(g)} className="text-xs px-2 py-1 rounded cursor-pointer hover:bg-gray-100 ml-1" style={{ color: '#D12229' }}>
                      <i className="ri-delete-bin-line text-xs"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <EquipmentModal
          type={type}
          assetId={assetId}
          endpoint={meta.endpoint}
          group={editingGroup}
          onClose={() => setModalOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

// ============================================================================
// Modal component (kept in same file for compactness)
// ============================================================================

interface ModalProps {
  type: EquipmentType;
  assetId: string;
  endpoint: string;
  group: Group | null;
  onClose: () => void;
  onSaved: () => void;
}

function EquipmentModal({ type, assetId, endpoint, group, onClose, onSaved }: ModalProps) {
  const { isDark } = useTheme();
  const clr = useEquipmentColors(isDark);
  const isEdit = !!group;
  const [form, setForm] = useState<Record<string, any>>(() => {
    if (group) return { ...group };
    return type === 'panel'
      ? { manufacturer: '', model: '', technology: '', nominalPowerW: '', panelCount: '', notes: '' }
      : { manufacturer: '', model: '', inverterCount: '', nominalPowerKw: '', modbusProtocol: '', modbusHost: '', modbusPort: '', modbusUnitId: '', unitIds: '', notes: '' };
  });
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  const set = (k: string, v: any) => setForm((prev) => ({ ...prev, [k]: v }));

  const save = useCallback(async (saveAndAddAnother: boolean = false) => {
    setSaving(true);
    try {
      const payload: Record<string, any> = {};
      for (const [k, v] of Object.entries(form)) {
        if (['nominalPowerW', 'panelCount', 'inverterCount', 'nominalPowerKw', 'modbusPort', 'modbusUnitId'].includes(k)) {
          payload[k] = v === '' || v == null ? null : Number(v);
        } else {
          payload[k] = v === '' ? null : v;
        }
      }
      if (isEdit) {
        await api.patch(`/assets/${assetId}/${endpoint}/${group!.id}`, payload);
        showToast({ type: 'success', message: 'Saved' });
        onSaved();
      } else {
        await api.post(`/assets/${assetId}/${endpoint}`, payload);
        showToast({ type: 'success', message: 'Added' });
        if (saveAndAddAnother) {
          setForm((prev) => {
            const fresh: Record<string, any> = type === 'panel'
              ? { manufacturer: prev.manufacturer || '', model: '', technology: '', nominalPowerW: '', panelCount: '', notes: '' }
              : { manufacturer: prev.manufacturer || '', model: '', inverterCount: '', nominalPowerKw: '', modbusProtocol: prev.modbusProtocol || '', modbusHost: prev.modbusHost || '', modbusPort: '', modbusUnitId: '', unitIds: '', notes: '' };
            return fresh;
          });
        } else {
          onSaved();
        }
      }
    } catch (e) {
      showToast({ type: 'error', message: getApiErrorMessage(e, 'Save failed') });
    } finally {
      setSaving(false);
    }
  }, [form, isEdit, assetId, endpoint, group, onSaved, showToast, type]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: clr.modalOverlay }} onClick={onClose}>
      <div className="rounded-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" style={{ background: clr.cardBg }} onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${clr.cardBorder}` }}>
          <h3 className="text-base font-semibold" style={{ color: clr.textMain }}>
            {isEdit ? 'Edit' : 'Add'} {type === 'panel' ? 'panel' : 'inverter'} group
          </h3>
          <button onClick={onClose} className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer" style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'transparent' }}>
            <i className="ri-close-line" style={{ color: clr.textMuted }}></i>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Equipment info */}
          <div>
            <div className="text-[11px] font-semibold uppercase mb-2" style={{ color: clr.mutedText }}>Equipment</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Manufacturer *" value={form.manufacturer} onChange={(v) => set('manufacturer', v)} placeholder="SMA, Longi Solar..." isDark={isDark} clr={clr} />
              <Field label="Model *" value={form.model} onChange={(v) => set('model', v)} placeholder="STP 50-40, LR-72PE..." isDark={isDark} clr={clr} />
              {type === 'panel' && (
                <>
                  <Field label="Technology" value={form.technology} onChange={(v) => set('technology', v)} placeholder="mono / poly / thin-film" isDark={isDark} clr={clr} />
                  <Field label="Nominal power, W *" type="number" value={form.nominalPowerW} onChange={(v) => set('nominalPowerW', v)} placeholder="365" isDark={isDark} clr={clr} />
                  <Field label="Panel count" type="number" value={form.panelCount} onChange={(v) => set('panelCount', v)} placeholder="optional" isDark={isDark} clr={clr} />
                </>
              )}
              {type === 'inverter' && (
                <>
                  <Field label="Nominal power, kW *" type="number" value={form.nominalPowerKw} onChange={(v) => set('nominalPowerKw', v)} placeholder="50" isDark={isDark} clr={clr} />
                  <Field label="Inverter count *" type="number" value={form.inverterCount} onChange={(v) => set('inverterCount', v)} placeholder="28" isDark={isDark} clr={clr} />
                </>
              )}
            </div>
          </div>

          {/* Modbus config — inverters only */}
          {type === 'inverter' && (
            <div>
              <div className="text-[11px] font-semibold uppercase mb-2" style={{ color: clr.mutedText }}>Modbus connection (optional)</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Protocol" value={form.modbusProtocol} onChange={(v) => set('modbusProtocol', v)} placeholder="sma-sunspec / huawei-smartlogger / fronius / growatt" isDark={isDark} clr={clr} />
                <Field label="Host" value={form.modbusHost} onChange={(v) => set('modbusHost', v)} placeholder="93.170.53.225" isDark={isDark} clr={clr} />
                <Field label="Port" type="number" value={form.modbusPort} onChange={(v) => set('modbusPort', v)} placeholder="502 / 5020 / 5023" isDark={isDark} clr={clr} />
                <Field label="Plant-aggregate unit_id" type="number" value={form.modbusUnitId} onChange={(v) => set('modbusUnitId', v)} placeholder="for SMA: 2; empty for per-inverter polling" isDark={isDark} clr={clr} />
                <Field label="Per-inverter unit_ids (CSV)" value={form.unitIds} onChange={(v) => set('unitIds', v)} placeholder="11,12,13,14,15..." wide isDark={isDark} clr={clr} />
              </div>
            </div>
          )}

          <div>
            <div className="text-[11px] font-semibold uppercase mb-2" style={{ color: clr.mutedText }}>Notes</div>
            <textarea
              value={form.notes ?? ''}
              onChange={(e) => set('notes', e.target.value)}
              rows={2}
              className="w-full text-xs px-3 py-2 rounded-md outline-none"
              style={{ border: `1px solid ${clr.inputBorder}`, color: clr.textMain, background: clr.inputBg }}
              placeholder="any operator notes"
            />
          </div>
        </div>

        <div className="px-6 py-4 flex items-center justify-end gap-2" style={{ borderTop: `1px solid ${clr.footerBorder}`, background: clr.footerBg }}>
          <button onClick={onClose} className="text-xs px-4 py-2 rounded-md cursor-pointer" style={{ background: clr.cancelBg, border: `1px solid ${clr.cardBorder}`, color: clr.textMain }}>
            Cancel
          </button>
          {!isEdit && (
            <button onClick={() => save(true)} disabled={saving} className="text-xs px-4 py-2 rounded-md cursor-pointer" style={{ background: clr.cancelBg, border: '1px solid #D12229', color: '#D12229' }}>
              Save & add another
            </button>
          )}
          <button onClick={() => save(false)} disabled={saving} className="text-xs px-4 py-2 rounded-md cursor-pointer font-medium" style={{ background: '#D12229', color: '#fff' }}>
            {saving ? 'Saving...' : (isEdit ? 'Save' : 'Add')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type, placeholder, wide, isDark, clr }: { label: string; value: any; onChange: (v: string) => void; type?: string; placeholder?: string; wide?: boolean; isDark: boolean; clr: ReturnType<typeof useEquipmentColors> }) {
  return (
    <label className={`block ${wide ? 'sm:col-span-2' : ''}`}>
      <span className="text-[11px] mb-1 block" style={{ color: clr.textMuted }}>{label}</span>
      <input
        type={type || 'text'}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full text-xs px-3 py-2 rounded-md outline-none"
        style={{ border: `1px solid ${clr.inputBorder}`, color: clr.textMain, background: clr.inputBg }}
      />
    </label>
  );
}